import {
    Button,
    STYLE_FILL,
    STYLE_LINK,
    TYPE_LINK,
} from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { NewWindowChunky } from '@eventbrite/eds-iconography';
import { withTooltip } from '@eventbrite/eds-tooltip';
import { gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { css, cx } from 'emotion';
import React from 'react';
import {
    GA_ACTION_ZOOM_APP_LOADED,
    GA_DEFAULT_CATEGORY,
    MODULE_TYPE_ZOOM_MAPPING,
} from '../../../constants';

export interface ZoomJoinButtonProps {
    buttonClass: string;
    children: React.ReactNode;
    dataTestId: string;
    disabled?: boolean;
    isLoading?: boolean;
    isNotSuccessful?: boolean;
    joinMinBefore: string;
    onClick: Function;
    style: string;
    type: string;
}

export const ZoomJoinButton: React.FC<ZoomJoinButtonProps> = ({
    buttonClass,
    children,
    dataTestId,
    disabled,
    isLoading,
    isNotSuccessful,
    joinMinBefore,
    onClick,
    style,
    type,
}) => {
    const ButtonWithTooltip = withTooltip(Button);
    const tooltipText = gettext(
        'This button will be enabled %(joinMinBefore)s minutes before the start of the event',
        { joinMinBefore },
    );

    return disabled ? (
        <ButtonWithTooltip
            __containerClassName={buttonClass}
            color="grey-800"
            data-testid={dataTestId}
            disabled={disabled}
            onClick={onClick}
            style={style}
            tooltipId="tooltip"
            tooltipStyle="basic"
            type={type}
            tooltipText={
                <div className={cx(styles.buttonTooltip)}>{tooltipText}</div>
            }
            tooltipArrowLocation="left"
        >
            {children}
        </ButtonWithTooltip>
    ) : (
        <Button
            __containerClassName={buttonClass}
            data-spec={dataTestId}
            disabled={disabled}
            isLoading={isLoading}
            isNotSuccessful={isNotSuccessful}
            onClick={onClick}
            style={style}
            type={type}
        >
            {children}
        </Button>
    );
};

export interface ZoomJoinInAppProps {
    isPrimaryButton: boolean;
    joinMinBefore: string;
    url: string;
    status: {
        hasEnded?: boolean;
        hasStarted?: boolean;
        isCountingDown?: boolean;
        isStarting?: boolean;
    };
}

export const ZoomJoinInApp: React.FC<ZoomJoinInAppProps> = ({
    isPrimaryButton,
    joinMinBefore,
    status,
    url,
}) => {
    const { hasEnded, isCountingDown } = status;
    const disabled = isCountingDown;
    const buttonClass = cx(styles.startZoomButton);
    const dataTestId = 'join-zoom-button';

    const startZoom = () => {
        trackAnalyticsEvent({
            action: GA_ACTION_ZOOM_APP_LOADED,
            category: GA_DEFAULT_CATEGORY,
            label: MODULE_TYPE_ZOOM_MAPPING,
        });
        if (url) window.open(url);
    };

    // TODO: move this logic to parent component
    if (hasEnded) return null;

    const renderButton = () => {
        let content, style;

        if (isPrimaryButton) {
            style = STYLE_FILL;
            content = gettext('Join now');
        } else {
            style = STYLE_LINK;
            content = (
                <div>
                    {gettext('Open in Zoom')}
                    <span className="new-window-icon-wrapper eds-l-mar-left-2">
                        <Icon
                            size="xsmall"
                            type={<NewWindowChunky />}
                            color={'ui-blue'}
                        />
                    </span>
                </div>
            );
        }

        return (
            <ZoomJoinButton
                buttonClass={buttonClass}
                dataTestId={dataTestId}
                disabled={disabled}
                onClick={startZoom}
                style={style}
                type={TYPE_LINK}
                joinMinBefore={joinMinBefore}
            >
                {content}
            </ZoomJoinButton>
        );
    };

    return renderButton();
};

const styles = {
    buttonTooltip: css`
        max-width: 240px;
    `,
    startZoomButton: css`
        transition: none;
    `,
};
